import React from 'react';
import { reportError } from '../utils/errorReporting';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to our error reporting service
    console.error('Error Boundary caught an error:', error, errorInfo);
    
    // Report to Google Error Reporting
    reportError(error, {
      functionName: 'ErrorBoundary',
      componentStack: errorInfo.componentStack,
      errorBoundary: true,
      props: this.props
    });

    // Update state with error details
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
  }

  handleReload = () => {
    // Clear error state and reload
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.reload();
  };

  handleGoHome = () => {
    // Clear error state and navigate to home
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.href = '/home';
  };

  render() {
    if (this.state.hasError) {
      // Fallback UI
      return (
        <div style={{
          padding: '40px',
          textAlign: 'center',
          maxWidth: '600px',
          margin: '0 auto',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        }}>
          <div style={{
            backgroundColor: '#fee',
            border: '1px solid #fcc',
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '20px'
          }}>
            <h2 style={{ color: '#c53030', marginTop: 0 }}>
              Oops! Something went wrong
            </h2>
            <p style={{ color: '#744210', fontSize: '16px', lineHeight: '1.5' }}>
              We're sorry, but something unexpected happened. The error has been automatically 
              reported to our team and we'll look into it.
            </p>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <button
              onClick={this.handleReload}
              style={{
                backgroundColor: '#3182ce',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '6px',
                fontSize: '16px',
                cursor: 'pointer',
                marginRight: '12px'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#2c5aa0'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#3182ce'}
            >
              Try Again
            </button>
            <button
              onClick={this.handleGoHome}
              style={{
                backgroundColor: '#38a169',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '6px',
                fontSize: '16px',
                cursor: 'pointer'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#2f855a'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#38a169'}
            >
              Go to Home
            </button>
          </div>

          {/* Show error details in development */}
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <details style={{ 
              textAlign: 'left', 
              backgroundColor: '#f7fafc', 
              padding: '16px', 
              borderRadius: '6px',
              border: '1px solid #e2e8f0',
              marginTop: '20px'
            }}>
              <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '8px' }}>
                Error Details (Development Mode)
              </summary>
              <pre style={{ 
                fontSize: '12px', 
                overflow: 'auto',
                color: '#2d3748',
                margin: 0
              }}>
                {this.state.error.toString()}
                {this.state.errorInfo.componentStack}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;